import { WatchfaceDeviceType, useWatchfaceDevices } from '@/hooks';
import { dataSourceWholeItems } from '@/resources/data-source';
import {
  CanUseAttr,
  dataSourceFormatDisabled,
  deviceFeatureRules as rules
} from '@/resources/device-feature-rules';
import { DataSourceFormat } from '@/resources/enum';
import { useWatchfaceStore } from '@/store/use-watchface-store';

/**
 * 判断属性是否支持
 * @param attr 属性
 * @param watchfaceType 【可选】表盘type，默认使用当前表盘
 */
export const canUse = (
  attr: CanUseAttr,
  deviceType?: string
): { valid: boolean; msg?: string } => {
  const watchfaceStroe = useWatchfaceStore();
  deviceType = deviceType ? deviceType : watchfaceStroe.description?.deviceType;

  const { wholeDevices } = useWatchfaceDevices();
  const watchfaceDevice = wholeDevices.value.find(
    (item) => item.deviceType == deviceType
  );

  if (!watchfaceDevice) {
    return {
      valid: false,
      msg: '未获取到表盘类型'
    };
  }
  // 如果是多配色的话，在非普通表盘下否可用
  if (
    attr == 'multi-color-group' &&
    (watchfaceStroe.description.watchfaceType ?? 'normal') != 'normal'
  ) {
    return { valid: true };
  }
  const _deviceType = watchfaceDevice.deviceType.toLowerCase();
  for (let i = 0; i < rules.length; i++) {
    const { name, includeTypes, excludeTypes, errMsg } = rules[i];
    if (name == attr) {
      // 如果不满足
      if (
        (includeTypes &&
          !includeTypes.some((item) => item.toLowerCase() == _deviceType)) ||
        (excludeTypes &&
          excludeTypes.some((item) => item.toLowerCase() == _deviceType))
      ) {
        return {
          valid: false,
          msg: errMsg
        };
      }
    }
  }
  return {
    valid: true
  };
};

export const canUseDataSource = (
  sourceKey: string | DataSourceItem,
  deviceType: WatchfaceDeviceType
) => {
  let sourceItem;
  if (typeof sourceKey == 'string') {
    if (sourceKey === 'timeHour24H') {
      sourceItem = dataSourceWholeItems.find(
        (item) => item.key == sourceKey && item.exportCheckIsSupport
      );
    } else {
      sourceItem = dataSourceWholeItems.find((item) => item.key == sourceKey);
    }
  } else {
    sourceItem = sourceKey;
  }
  if (!sourceItem) return false;
  const { includeDeviceTypes, excludeDeviceTypes } = sourceItem;

  let res = true;
  if (includeDeviceTypes && !includeDeviceTypes.includes(deviceType)) {
    res = false;
  }
  if (excludeDeviceTypes && excludeDeviceTypes.includes(deviceType)) {
    res = false;
  }
  // 如果需要通过widget拼接，则需要判断是否支持自动布局
  if (sourceItem.widgetImageArray && !canUse('widget-flex').valid) {
    res = false;
  }
  return res;
};

export const disabledDataSourceFormat = (
  sourceKey: string,
  format: DataSourceFormat
) => {
  if (canUse('tempature-with-unit').valid) return false;
  const sourceItem = dataSourceFormatDisabled.find(
    (item) => item.key == sourceKey
  );
  if (!sourceItem) return false;
  const { disabledFormats } = sourceItem;
  return disabledFormats?.includes(format);
};

/**
 * 判断数据源是否对图层支持
 * @param sourceKey
 * @param deviceType
 * @returns
 */
export const canUseDataSourceByShape = (data: {
  sourceKey: string;
  shapeType:
    | 'text'
    | 'image'
    | 'image-array-normal'
    | 'image-array-digit'
    | 'progress-bar';
  deviceType: WatchfaceDeviceType;
  themeType: 'normal' | 'AOD';
}) => {
  const { sourceKey, shapeType, deviceType, themeType } = data;

  const sourceItemParent = dataSourceWholeItems.find(
    (item) => !!item.children?.includes(sourceKey)
  );
  const _sourceItem = dataSourceWholeItems.find(
    (item) => item.key == sourceKey
  );
  // 如果存在父级，则取父级
  const sourceItem = sourceItemParent || _sourceItem;

  if (!sourceItem) return false;
  const { allowShapeTypes, allowShapeTypeFn } = sourceItem;

  let valid = true;

  // 首先验证allowShapeTypes
  if (!allowShapeTypes?.length) valid = false;
  if (allowShapeTypes?.length) {
    if (allowShapeTypes.every((item) => item != '*' && item != shapeType)) {
      valid = false;
    }
  }
  if (!valid) return false;

  // 如果allowShapeTypeFn存在，继续验证
  if (allowShapeTypeFn) {
    if (!allowShapeTypeFn({ shapeType, deviceType, themeType })) {
      valid = false;
    }
  }
  return valid;
};
